import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Search, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClientCombobox = ({ value, onSelect, onClear, placeholder = "Buscar por nombre, nombre comercial o folio…", testid = "client-combobox", className = "" }) => {
  const [query, setQuery] = useState(null); // null = sin edición activa, muestra `value`
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  const search = useCallback((q) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/clients/search`, { params: { q } });
        setResults(r.data || []);
      } catch { setResults([]); }
    }, 250);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        // Al salir sin elegir: no dejar texto libre en el campo
        setQuery((q) => {
          if (q !== null && q.trim() !== "" && q !== value) setNotFound(true);
          return null;
        });
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [value]);

  const handleChange = (v) => {
    setQuery(v);
    setNotFound(false);
    setOpen(true);
    search(v);
    if (v.trim() === "" && onClear) onClear();
  };

  const pick = (c) => {
    setQuery(null);
    setOpen(false);
    setNotFound(false);
    onSelect(c);
  };

  const shown = query !== null ? query : (value || "");

  return (
    <div className={`relative ${className}`} ref={ref}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <Input
          value={shown}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => { setOpen(true); search(query !== null ? query : ""); }}
          placeholder={placeholder}
          className="pl-10"
          autoComplete="off"
          data-testid={testid}
        />
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {results.length > 0 ? results.map((c) => (
            <button key={c.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              className="w-full px-3 py-2 text-left hover:bg-slate-100 border-b last:border-b-0"
              data-testid={`${testid}-option-${c.id}`}>
              <p className="font-medium text-sm text-slate-900">{c.company_name}</p>
              <p className="text-xs text-slate-500">{[c.nombre_comercial, c.folio].filter(Boolean).join(" – ")}</p>
            </button>
          )) : (query || "").trim().length >= 2 && (
            <div className="p-3 text-sm text-slate-500 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <span>Este cliente no existe en la base. Solicita el alta al área correspondiente.</span>
            </div>
          )}
        </div>
      )}
      {notFound && (
        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1" data-testid={`${testid}-not-found`}>
          <AlertTriangle className="w-3.5 h-3.5" /> Este cliente no existe en la base. Solicita el alta al área correspondiente.
        </p>
      )}
    </div>
  );
};

export default ClientCombobox;
